import {MetaDataAttributeDto} from '../dto/meta-data-attribute.dto';
import {MetaDataAttributeTypeEnum} from '../enum/meta-data-attribute-type.enum';

/**
 * The ONE place that classifies a `MetaDataAttributeDto` into the QL field-name families the builder
 * emits — so no consumer compares `attributeType` itself.
 *
 * Why a reader and not a plain comparison: `GET /mvsa/core/meta/byAlias/{alias}` returns the
 * `MetaDataEntity` graph directly, and `MetaDataAttributeType` is an `AlphaBaseEnum`, so
 * `attributeType` arrives as the ORDINAL (`0` regular, `1` extended_object, `2` generic_type_attribute,
 * `3` generic_value, `null` for unclassified members such as embedded types). The by-id endpoint goes
 * through `MetaDataMapper.toAttributeDto`, which sends the enum NAME instead. Comparing against the
 * string name silently classified every generic field as regular on the byAlias path (the builder then
 * emitted `4711` instead of `g_4711:value`), which is the bug this reader closes. It accepts both wire
 * shapes and additionally uses `genericAttributeId` — set by `MetaService` for exactly the generic and
 * EAV members — as the primary signal, so a missing or unknown `attributeType` cannot demote a generic
 * field to a regular one.
 */
export class MetaDataAttributeKind {

    /** The attribute type as the enum, whether it came as ordinal or as name; `null` when absent or unknown. */
    static typeOf(attribute: MetaDataAttributeDto | null | undefined): MetaDataAttributeTypeEnum | null {
        const raw: unknown = attribute?.attributeType;
        if (raw == null) {
            return null;
        }
        if (typeof raw === 'number') {
            return Number.isInteger(raw) && typeof MetaDataAttributeTypeEnum[raw] === 'string'
                ? raw as MetaDataAttributeTypeEnum
                : null;
        }
        if (typeof raw === 'string') {
            const byName = MetaDataAttributeTypeEnum[raw as keyof typeof MetaDataAttributeTypeEnum];
            return typeof byName === 'number' ? byName : null;
        }
        return null;
    }

    /**
     * A scalar TypeField pool attribute (`generic_type_attribute`): QL name `g_<genericAttributeId>:value`.
     * `genericAttributeId` decides when the type is missing; an explicit `extended_object` never counts.
     */
    static isGenericAttribute(attribute: MetaDataAttributeDto | null | undefined): boolean {
        if (!attribute) {
            return false;
        }
        const type = MetaDataAttributeKind.typeOf(attribute);
        if (type === MetaDataAttributeTypeEnum.extended_object) {
            return false;
        }
        return type === MetaDataAttributeTypeEnum.generic_type_attribute || attribute.genericAttributeId != null;
    }

    /** An ObjectTypeAttribute member (`extended_object`, EAV): QL name `o_<attributeName>:value`. */
    static isEavAttribute(attribute: MetaDataAttributeDto | null | undefined): boolean {
        return MetaDataAttributeKind.typeOf(attribute) === MetaDataAttributeTypeEnum.extended_object;
    }

    /**
     * A computed member of the `+` (extended service) or `_` (extended config) family — the builder's
     * "Erweiterte Felder (berechnet)", modelled as an extended node, never as a plain field. Not to be
     * confused with the backend's `EntityMetaAttribute.isExtended()`, which means generic/EAV.
     */
    static isExtendedAttribute(attribute: MetaDataAttributeDto | null | undefined): boolean {
        return !!attribute && (!!attribute.extendedService || !!attribute.extendedConfig);
    }

    /** Everything the builder treats as a plain column: not generic, not EAV, not computed. */
    static isRegularAttribute(attribute: MetaDataAttributeDto | null | undefined): boolean {
        return !!attribute
            && !MetaDataAttributeKind.isGenericAttribute(attribute)
            && !MetaDataAttributeKind.isEavAttribute(attribute)
            && !MetaDataAttributeKind.isExtendedAttribute(attribute);
    }

    /**
     * The TypeField id of a generic attribute: `genericAttributeId`, or the numeric `attributeName`
     * (`MetaService` names generic members after their id) when the id was not delivered. `null` for a
     * non-generic attribute or when neither is usable.
     */
    static genericAttributeIdOf(attribute: MetaDataAttributeDto | null | undefined): number | null {
        if (!MetaDataAttributeKind.isGenericAttribute(attribute)) {
            return null;
        }
        if (attribute.genericAttributeId != null) {
            return attribute.genericAttributeId;
        }
        const fromName = Number(attribute.attributeName);
        return Number.isInteger(fromName) && fromName > 0 ? fromName : null;
    }

    /**
     * The QL name of a scalar attribute, as the field sections name it: `g_<id>:value` for a generic
     * attribute, `o_<name>:value` for an EAV attribute, the plain attribute name otherwise.
     */
    static scalarFieldName(attribute: MetaDataAttributeDto): string {
        if (MetaDataAttributeKind.isGenericAttribute(attribute)) {
            return `g_${MetaDataAttributeKind.genericAttributeIdOf(attribute) ?? attribute.attributeName}:value`;
        }
        if (MetaDataAttributeKind.isEavAttribute(attribute)) {
            return `o_${attribute.attributeName}:value`;
        }
        return attribute.attributeName;
    }
}
